import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Card,
  CardMedia,
  CircularProgress,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ReactCrop, { Crop, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useCreate } from "@refinedev/core";

interface ImageUploadWithCropProps {
  onImageUpload: (imageUrl: string) => void;
  existingImageUrl?: string;
  aspectRatio?: number;
  minWidth?: number;
  minHeight?: number;
  recommendedWidth?: number;
  recommendedHeight?: number;
  minCropWidth?: number;
  minCropHeight?: number;
  uploadResource?: string; // Resource endpoint for upload
}

export const ImageUploadWithCrop: React.FC<ImageUploadWithCropProps> = ({
  onImageUpload,
  existingImageUrl,
  aspectRatio = 2,
  minWidth = 900,
  minHeight = 450,
  recommendedWidth = 1200,
  recommendedHeight = 600,
  minCropWidth = 600,
  minCropHeight = 300,
  uploadResource = "grant-vestments/upload",
}) => {
  const { mutate: uploadImage } = useCreate();
  const [imageSrc, setImageSrc] = useState<string>("");
  const [crop, setCrop] = useState<Crop>({
    unit: "%",
    width: 70,
    height: 70 / aspectRatio,
    x: 15,
    y: 15,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [isCropDialogOpen, setIsCropDialogOpen] = useState(false);
  const [imageQualityWarning, setImageQualityWarning] = useState<string>("");
  const [dimensionError, setDimensionError] = useState<string>("");
  const [canCrop, setCanCrop] = useState<boolean>(true);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [isGeneratingPreview, setIsGeneratingPreview] =
    useState<boolean>(false);
  const [isUploadLoading, setIsUploadLoading] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const validateImageDimensions = (
    image: HTMLImageElement
  ): { isValid: boolean; warning?: string; error?: string } => {
    const width = image.naturalWidth;
    const height = image.naturalHeight;

    // if (width < 400 || height < 320) {
    //   return {
    //     isValid: false,
    //     error: `Image is too small to crop properly. Minimum size: 400x320px. Your image: ${width}x${height}px`,
    //   };
    // }

    if (width < minWidth || height < minHeight) {
      return {
        isValid: true,
        warning: `For best quality, upload images at least ${minWidth}x${minHeight}px. Your image: ${width}x${height}px may appear pixelated when cropped.`,
      };
    }

    if (width < recommendedWidth || height < recommendedHeight) {
      return {
        isValid: true,
        warning: `For optimal quality, upload images at least ${recommendedWidth}x${recommendedHeight}px (${aspectRatio}:1 ratio).`,
      };
    }

    return { isValid: true };
  };

  const getCroppedImg = async (
    image: HTMLImageElement,
    crop: PixelCrop
  ): Promise<Blob> => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Canvas context not available");
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const croppedWidth = crop.width * scaleX;
    const croppedHeight = crop.height * scaleY;

    canvas.width = croppedWidth;
    canvas.height = croppedHeight;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      croppedWidth,
      croppedHeight,
      0,
      0,
      croppedWidth,
      croppedHeight
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Canvas is empty"));
          }
        },
        "image/png",
        1
      );
    });
  };

  const getCroppedImgPreview = async (
    image: HTMLImageElement,
    crop: PixelCrop
  ): Promise<string> => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Canvas context not available");
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const croppedWidth = crop.width * scaleX;
    const croppedHeight = crop.height * scaleY;

    canvas.width = croppedWidth;
    canvas.height = croppedHeight;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      croppedWidth,
      croppedHeight,
      0,
      0,
      croppedWidth,
      croppedHeight
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(URL.createObjectURL(blob));
          } else {
            reject(new Error("Canvas is empty"));
          }
        },
        "image/png",
        1
      );
    });
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert("Image file size must be less than 10MB");
        return;
      }

      if (!file.type.startsWith("image/")) {
        alert("Please select a valid image file");
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result as string);
        setCrop({
          unit: "%",
          width: 70,
          height: 70 / aspectRatio,
          x: 15,
          y: 15,
        });
        setCompletedCrop(null);
        setPreviewUrl("");
        setDimensionError("");
        setImageQualityWarning("");
        setCanCrop(true);
        setIsCropDialogOpen(true);
      };
      reader.onerror = () => {
        alert("Failed to read image file");
      };
      reader.readAsDataURL(file);
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const image = e.currentTarget;
    imgRef.current = image;

    const validation = validateImageDimensions(image);

    if (!validation.isValid && validation.error) {
      setDimensionError(validation.error);
      setCanCrop(false);
    } else {
      setDimensionError("");
      setCanCrop(true);
    }

    if (validation.warning) {
      setImageQualityWarning(validation.warning);
    } else {
      setImageQualityWarning("");
    }
  };

  useEffect(() => {
    if (completedCrop && imgRef.current && !dimensionError) {
      generatePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedCrop, dimensionError]);

  const generatePreview = async () => {
    if (!completedCrop || !imgRef.current) return;

    try {
      setIsGeneratingPreview(true);
      const preview = await getCroppedImgPreview(imgRef.current, completedCrop);
      setPreviewUrl(preview);
    } catch (error) {
      console.error("Preview generation error:", error);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const handleCropConfirm = async () => {
    if (!completedCrop || !imgRef.current) {
      alert("Please select a crop area");
      return;
    }

    try {
      setIsUploadLoading(true);
      const croppedImageBlob = await getCroppedImg(
        imgRef.current,
        completedCrop
      );

      // Convert blob to file
      const timestamp = new Date().getTime();
      const croppedFile = new File(
        [croppedImageBlob],
        `grant-image-${timestamp}.png`,
        {
          type: "image/png",
        }
      );

      // Upload to server
      uploadImage(
        {
          resource: uploadResource,
          values: {
            image: croppedFile,
          },
          successNotification: false,
        },
        {
          onSuccess: (data) => {
            setIsUploadLoading(false);
            console.log("Upload success:", data);
            onImageUpload(data.data as any);
            setIsCropDialogOpen(false);
            setImageSrc("");
            setCompletedCrop(null);
            setPreviewUrl("");
            setDimensionError("");
            setImageQualityWarning("");
            setCanCrop(true);
          },
          onError: (error) => {
            console.error("Upload error:", error);
            alert("Oops! something went wrong with upload! Please try later");
            setIsUploadLoading(false);
          },
        }
      );
    } catch (error) {
      console.error("Crop error:", error);
      alert("Failed to crop image. Please try again.");
      setIsUploadLoading(false);
    }
  };

  const handleCropCancel = () => {
    setIsCropDialogOpen(false);
    setImageSrc("");
    setCrop({
      unit: "%",
      width: 70,
      height: 70 / aspectRatio,
      x: 15,
      y: 15,
    });
    setCompletedCrop(null);
    setPreviewUrl("");
    setDimensionError("");
    setImageQualityWarning("");
    setCanCrop(true);
  };

  return (
    <>
      <Stack direction="row" gap={4} flexWrap="wrap">
        <label htmlFor="image-input">
          <input
            id="image-input"
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleImageSelect}
          />
          <LoadingButton
            loading={isUploadLoading}
            loadingPosition="end"
            endIcon={<FileUploadIcon />}
            variant="contained"
            component="span"
          >
            Upload & Crop Image
          </LoadingButton>
          <br />
          {existingImageUrl && (
            <Typography variant="caption" color="green">
              ✓ Image uploaded successfully (Cropped to {aspectRatio}:1 ratio)
            </Typography>
          )}
        </label>
      </Stack>

      <Dialog
        open={isCropDialogOpen}
        onClose={handleCropCancel}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Crop Image to {aspectRatio}:1 Ratio
          <Typography variant="subtitle2" color="text.secondary">
            Drag to select the area you want to keep • Final image will be{" "}
            {aspectRatio}:1 ratio
          </Typography>
        </DialogTitle>
        <DialogContent>
          {dimensionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dimensionError}
            </Alert>
          )}

          {imageQualityWarning && !dimensionError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {imageQualityWarning}
            </Alert>
          )}

          {!dimensionError && !imageQualityWarning && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Select the area you want to keep. The crop will be forced to{" "}
              {aspectRatio}:1 ratio.
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
                backgroundColor: dimensionError ? "#ffebee" : "#000",
                borderRadius: 1,
                p: 2,
                opacity: dimensionError ? 0.6 : 1,
              }}
            >
              {imageSrc && (
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={aspectRatio}
                  locked={!!dimensionError}
                  disabled={!!dimensionError}
                >
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    alt="Crop preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "350px",
                      display: "block",
                    }}
                    onLoad={onImageLoad}
                  />
                </ReactCrop>
              )}
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                Preview in Card
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This is how your image will appear:
              </Typography>

              {isGeneratingPreview ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                  }}
                >
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Generating preview...
                  </Typography>
                </Box>
              ) : previewUrl ? (
                <Card sx={{ maxWidth: 300 }}>
                  <Box
                    sx={{
                      width: "100%",
                      position: "relative",
                      aspectRatio: `${aspectRatio}`,
                      overflow: "hidden",
                      borderRadius: 1,
                    }}
                  >
                    <CardMedia
                      component="img"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                      }}
                      image={previewUrl}
                      alt="Cropped preview"
                    />
                  </Box>
                </Card>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                    border: "2px dashed #ccc",
                    borderRadius: 1,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Adjust the crop to see preview
                  </Typography>
                </Box>
              )}

              {previewUrl && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Final image will be cropped to {aspectRatio}:1 ratio
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCropCancel} color="inherit">
            Cancel
          </Button>
          <LoadingButton
            onClick={handleCropConfirm}
            loading={isUploadLoading}
            variant="contained"
            color="primary"
            disabled={!completedCrop || !!dimensionError || !canCrop}
          >
            {dimensionError ? "Image Too Small" : "Confirm & Upload"}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
